import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AppProvider } from "./context/AppContext";
import { Header } from "./components/site/Header";
import { Footer } from "./components/site/Footer";
import { MobileBar } from "./components/site/MobileBar";
import { FinancePopup } from "./components/site/FinancePopup";
import HomePage from "./pages/HomePage";
import TechnologyPage from "./pages/TechnologyPage";
import TestDrivePage from "./pages/TestDrivePage";
import NetworkPage from "./pages/NetworkPage";
import GuidesPage from "./pages/GuidesPage";
import GuidePage from "./pages/GuidePage";
import PreorderPage from "./pages/PreorderPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import PrivacyPage from "./pages/PrivacyPage";
import CookiePage from "./pages/CookiePage";
import WithdrawalPage from "./pages/WithdrawalPage";
import FinancePage from "./pages/FinancePage";
import HospitalityPage, { HospitalityPageIt } from "./pages/HospitalityPage";
import CommercialLanding from "./pages/CommercialLanding";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminPage from "./pages/admin/AdminPage";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 350);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function SeoManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const url = `https://swapa.it${pathname}`;
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", url);
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement("meta");
      ogUrl.setAttribute("property", "og:url");
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute("content", url);
  }, [pathname]);
  return null;
}

function SiteLayout() {
  return (
    <>
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
      <FinancePopup />
      <MobileBar />
    </>
  );
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App bg-ink text-white">
      <div className="grain-overlay" aria-hidden="true" />
      <BrowserRouter>
        <AppProvider>
          <ScrollManager />
          <SeoManager />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/tecnologia" element={<TechnologyPage />} />
              <Route path="/test-drive" element={<TestDrivePage />} />
              <Route path="/network" element={<NetworkPage />} />
              <Route path="/guide" element={<GuidesPage />} />
              <Route path="/guide/:slug" element={<GuidePage />} />
              <Route path="/preordina" element={<PreorderPage />} />
              <Route path="/pagamento/successo" element={<PaymentSuccessPage />} />
              <Route path="/pagamento/annullato" element={<PaymentCancelPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/cookie" element={<CookiePage />} />
              <Route path="/recesso" element={<WithdrawalPage />} />
              <Route path="/finanziamento-swapa-zip" element={<FinancePage />} />
              <Route path="/hospitality" element={<HospitalityPage />} />
              <Route path="/hospitality/it" element={<HospitalityPageIt />} />
              <Route path="/microcar-elettrica" element={<CommercialLanding pageKey="microcar-elettrica" />} />
              <Route path="/microcar-per-ragazzi" element={<CommercialLanding pageKey="microcar-per-ragazzi" />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
          <Toaster theme="dark" position="top-center" toastOptions={{ style: { background: "#121215", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 2 } }} />
        </AppProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
